import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_CONFIG from '../config/apiConfig'; // Make sure you have this config file

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    identifier: '', // Can be email or username
    password: ''
  });
  const [otp, setOtp] = useState('');
  const [loginStep, setLoginStep] = useState<'credentials' | 'otp'>('credentials');
  
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handles the primary login with password
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.identifier || !formData.password) {
      setError("Please enter both identifier and password");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/auth/login/password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier: formData.identifier,
          password: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Invalid credentials');
      }
      
      const token = data.token;
      // CORRECTED: Build userPayload from the flat response structure
      const userPayload = {
        id: data.id,
        email: data.email,
          name: data.name
      };

      if (!token || !userPayload.id) {
        throw new Error('Login response is missing token or user data.');
      }

      await login(token, userPayload);
      localStorage.setItem('user', JSON.stringify(userPayload));
      
      navigate('/', { replace: true });

    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handles the request to send an OTP for login
  const handleOtpRequest = async () => {
    setError('');
    setSuccess('');

    if (!formData.identifier) {
      setError("Please enter your email or username to receive an OTP");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/auth/login/otp-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ identifier: formData.identifier })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to request OTP.');
      }
      
      setSuccess(data.message);
      setLoginStep('otp'); // Switch to OTP verification view
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handles the OTP verification for login
  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!otp) {
      setError("Please enter the OTP.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/auth/login/otp-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier: formData.identifier,
          otp
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Invalid or expired OTP.');
      }

      const token = data.token;
      // CORRECTED: Build userPayload from the flat response structure
      const userPayload = {
        id: data.id,
        email: data.email,
        name: data.name
      };
      
      if (!token || !userPayload.id) {
        throw new Error('Login response is missing token or user data.');
      }

      await login(token, userPayload);
      localStorage.setItem('user', JSON.stringify(userPayload));
      
      navigate('/', { replace: true });

    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handles resending OTP
  const handleResendOtp = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
        const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/auth/resend-otp`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email: formData.identifier }) // API expects 'email' key
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to resend OTP');
        }

        setSuccess(data.message);
    } catch (err: any) {
        setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-lg w-full space-y-8 bg-white/90 backdrop-blur-lg p-10 rounded-3xl shadow-2xl border border-white/20">
          <div className="text-center">
            <h2 className="mt-2 text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              {loginStep === 'credentials' ? 'Welcome Back' : 'Enter OTP'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {loginStep === 'credentials' 
                ? 'Enter your credentials to access your account' 
                : `An OTP was sent to ${formData.identifier}`}
            </p>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl text-sm flex items-center">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
              {success}
            </div>
          )}

          {/* LOGIN WITH CREDENTIALS FORM */}
          {loginStep === 'credentials' && (
            <form className="mt-8 space-y-6" onSubmit={handlePasswordSubmit}>
              <div className="space-y-5">
                <div className="relative group">
                  <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-2">
                    Email or Username
                  </label>
                  <div className="relative">
                    <input
                      id="identifier"
                      name="identifier"
                      type="text"
                      autoComplete="username"
                      required
                      className="w-full px-4 py-4 border border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 group-hover:shadow-lg"
                      placeholder="Enter your email or username"
                      value={formData.identifier}
                      onChange={handleChange}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center"><svg className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg></div>
                  </div>
                </div>

                <div className="relative group">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <input id="password" name="password" type={showPassword ? "text" : "password"} autoComplete="current-password" required
                      className="w-full px-4 py-4 border border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 group-hover:shadow-lg"
                      placeholder="Enter your password" value={formData.password} onChange={handleChange} />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <svg className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> : <svg className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={isLoading} className="w-full py-4 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl">
                  {isLoading ? <div className="flex items-center justify-center"><svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Signing in...</div> : <div className="flex items-center justify-center"><svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>Sign In</div>}
                </button>
                
                <div className="text-center">
                    <button type="button" onClick={handleOtpRequest} className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                        Login with OTP instead
                    </button>
                </div>
              </div>
            </form>
          )}

          {/* LOGIN WITH OTP FORM */}
          {loginStep === 'otp' && (
            <form className="mt-8 space-y-6" onSubmit={handleOtpVerify}>
                <div className="relative group">
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                    One-Time Password (OTP)
                  </label>
                  <input id="otp" name="otp" type="text" required
                    className="w-full px-4 py-4 border border-gray-200 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 group-hover:shadow-lg"
                    placeholder="Enter the OTP from your email" value={otp} onChange={(e) => setOtp(e.target.value)} />
                </div>
                <button type="submit" disabled={isLoading} className="w-full py-4 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl">
                  {isLoading ? 'Verifying...' : 'Verify & Sign In'}
                </button>

                <div className="text-center flex justify-between items-center">
                    <button type="button" onClick={handleResendOtp} className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                        Resend OTP
                    </button>
                     <button type="button" onClick={() => { setLoginStep('credentials'); setError(''); setSuccess(''); }} className="font-medium text-gray-600 hover:text-gray-500 transition-colors">
                        Back to Password
                    </button>
                </div>
            </form>
          )}

          <div className="text-center pt-4">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-300">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden">
        {/* Decorative right side panel remains the same */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-indigo-600/20 to-purple-600/20 backdrop-blur-3xl"></div>
        <div className="relative z-10 text-center text-white p-12">
          {/* ... existing SVG and text ... */}
        </div>
      </div>
    </div>
  );
};

export default Login;